/**
 * Transform types and constants for DrawingJS.
 */

import type { Point2D, Point3D } from "../types/types";
import type { HandleIcon, HandleShape } from "./handleShapes";
export type { Point2D, Point3D };

/**
 * Key points calculated from entity bounds.
 */
export interface KeyPoints {
  center: Point3D;
  topLeft: Point3D;
  topRight: Point3D;
  bottomLeft: Point3D;
  bottomRight: Point3D;
  topMiddle: Point3D;
  bottomMiddle: Point3D;
  leftMiddle: Point3D;
  rightMiddle: Point3D;
  min: Point3D;
  max: Point3D;
}

/**
 * Handle types for transformation.
 */
export enum HandleType {
  Translation = "translation",
  Rotation = "rotation",
  Scale = "scale",
  Resize = "resize",
  Vertex = "vertex",
  AddLeader = "addLeader",
  DeleteLeader = "deleteLeader",
  /** Dynamic block: toggles a two-state property (flip). */
  DynFlip = "dynFlip",
  /** Dynamic block: opens the value list of a constrained property (visibility, presets). */
  DynList = "dynList",
  /** Dynamic block: drags an XY point property. */
  DynPoint = "dynPoint",
  /** Dynamic block: opens the dial of a rotation property. */
  DynAngle = "dynAngle",
}

/**
 * Handle position identifiers.
 */
export enum HandlePosition {
  Center = "center",
  TopLeft = "topLeft",
  TopRight = "topRight",
  BottomLeft = "bottomLeft",
  BottomRight = "bottomRight",
  TopMiddle = "topMiddle",
  BottomMiddle = "bottomMiddle",
  LeftMiddle = "leftMiddle",
  RightMiddle = "rightMiddle",
  Rotation = "rotation",
  AddLeader = "addLeader",
  DeleteLeader = "deleteLeader",
  /** Dynamic block property handle — several may coexist, keyed by property name. */
  DynProperty = "dynProperty",
}

/**
 * Axis constraints for resize handles.
 */
export interface ResizeAxes {
  x?: boolean;
  y?: boolean;
}

/**
 * Context passed to handles during transform operations.
 */
export interface TransformContext {
  /** Current screen coordinates */
  screenPoint: Point2D;
  /** Current world coordinates */
  worldPoint: Point3D;
  /** Start screen coordinates */
  startScreenPoint: Point2D;
  /** Start world coordinates */
  startWorldPoint: Point3D;
  /** Shift key pressed */
  shiftKey: boolean;
  /** Ctrl key pressed */
  ctrlKey: boolean;
}

/**
 * Transform data for a single entity (used during transform operation).
 */
export interface TransformData {
  /** Original entity ID */
  entityId: import("../types/DrawingWeb").DrawingModule.OdDbObjectId;
  /** Original entity handle */
  handle: string;
  /** Original transform matrix values (simplified - translation only) */
  originalMatrix: number[];
  /** Ghost entity for preview (added directly to view, not database) */
  ghostEntity?: import("../types/DrawingWeb").DrawingModule.OdDbEntity;
}

/**
 * Handle draw options.
 */
export interface HandleDrawOptions {
  /** Fill color [r, g, b] */
  fillColor: [number, number, number];
  /** Stroke color [r, g, b] */
  strokeColor: [number, number, number];
  /** Radius in screen pixels */
  radius: number;
  /** Icon to draw inside handle */
  icon?: "move" | "rotate" | "scale";
  /**
   * Silhouette of the handle. Defaults to a circle when omitted — dynamic block
   * handles use the AutoCAD glyphs instead (see transform/handleShapes.ts).
   */
  shape?: HandleShape;
  /** White line icon drawn inside the handle (see transform/handleShapes.ts). */
  innerIcon?: HandleIcon;
  /**
   * World point the handle is tied to by a dashed line, in the style of the
   * bounding box — for a handle standing apart from the box it acts on.
   */
  tether?: Point3D;
}

/**
 * Theme colors for transform UI.
 */
export const TRANSFORM_THEME = {
  /** Grid perimeter color */
  gridPerimeter: [0, 123, 244] as [number, number, number],
  /** Handle fill color */
  handleFill: [0, 123, 244] as [number, number, number],
  /** Handle stroke color */
  handleStroke: [255, 255, 255] as [number, number, number],
  /** Rotation handle color */
  rotationHandle: [0, 200, 100] as [number, number, number],
  /** Scale handle color */
  scaleHandle: [200, 100, 0] as [number, number, number],
  /** Vertex handle color (purple/magenta) */
  vertexHandle: [180, 80, 200] as [number, number, number],
  /** Add leader handle color (green) */
  addLeaderHandle: [50, 180, 80] as [number, number, number],
  /** Ghost entity opacity */
  ghostOpacity: 0.5,
};

/**
 * Handle sizes in screen pixels.
 */
export const HANDLE_SIZES = {
  /** used to scale handle at runtime */
  handleScale: 1,
  /** Default handle radius */
  default: 12,
  /** Translation handle radius */
  translation: 18,
  /** Rotation handle radius */
  rotation: 18,
  /** Scale handle radius */
  scale: 12,
  /** Vertex handle radius (smaller) */
  vertex: 12,
  /** Hit test tolerance */
  hitTolerance: 24,
};

/**
 * Handle visibility priority (higher number = higher priority = always shown first).
 * When handles overlap, lower priority handles are hidden.
 *
 * Priority order (highest to lowest):
 * 1. Translation - most important, always visible
 * 2. Rotation - second most important
 * 3. Scale (uniform) - third priority
 * 4. Vertex - fourth priority (polyline editing)
 * 5. Resize - lowest priority (non-uniform scaling)
 */
export const HANDLE_PRIORITY: Record<HandleType, number> = {
  [HandleType.Translation]: 100,
  [HandleType.Rotation]: 80,
  [HandleType.AddLeader]: 70,
  [HandleType.DeleteLeader]: 65,
  [HandleType.Scale]: 60,
  // Dynamic block handles sit above Resize: they carry block-specific meaning the
  // generic bounding-box handles do not, so they win when they overlap.
  [HandleType.DynFlip]: 55,
  [HandleType.DynList]: 54,
  [HandleType.DynPoint]: 53,
  [HandleType.DynAngle]: 52,
  [HandleType.Vertex]: 40,
  [HandleType.Resize]: 20,
};

/**
 * Minimum screen distance (in pixels) between handles before they are considered overlapping.
 * If two handles are closer than this, the lower priority handle will be hidden.
 */
export const HANDLE_OVERLAP_THRESHOLD = 10;

/**
 * Handle offset from entity extent in screen pixels.
 * This ensures handles stay at a consistent visual distance regardless of zoom.
 */
export const HANDLE_OFFSET_PIXELS = 50;

/**
 * Minimum offset of the AddLeaderHandle from the text extent, in screen pixels.
 * Prevents the handle from merging into the text edge when zoomed out.
 */
export const ADD_LEADER_HANDLE_OFFSET_PIXELS = 50;

/**
 * Default handle draw options.
 */
export const DEFAULT_HANDLE_DRAW_OPTIONS: HandleDrawOptions = {
  fillColor: TRANSFORM_THEME.handleFill,
  strokeColor: TRANSFORM_THEME.handleStroke,
  radius: HANDLE_SIZES.default * HANDLE_SIZES.handleScale,
};

/**
 * Add leader handle draw options.
 */
export const ADD_LEADER_HANDLE_DRAW_OPTIONS: HandleDrawOptions = {
  fillColor: TRANSFORM_THEME.addLeaderHandle,
  strokeColor: TRANSFORM_THEME.handleStroke,
  radius: HANDLE_SIZES.default * HANDLE_SIZES.handleScale,
};
